import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import TvRounded from "@mui/icons-material/TvRounded";
import { useCallback, useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { Link } from "react-router-dom";

import RevaText from "components/common/RevaText";
import { adminEventRoute, adminEventTournamentsRoute, publicEventBoardRoute } from "constants/routes";
import {
  competitionsGenerateDynamicLinkRoute,
  competitionsShowRoute,
  competitionsToggleBoardRoute,
  competitionsTogglePublishedRoute,
} from "../../../network/api-routes";
import performRequest from "../../../network/perform-request";
import { withUserContext } from "../../hoc/withUserContext";
import { withSnackbar } from "notistack";

const ChecklistItem = ({ label, actionLabel, to, checked, onNavigate }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.75 }}>
    {checked ? (
      <CheckCircleRounded color="success" fontSize="small" />
    ) : (
      <RadioButtonUncheckedRounded color="disabled" fontSize="small" />
    )}
    <RevaText
      id={label}
      variant="body2"
      color={checked ? "textPrimary" : "textSecondary"}
      sx={{ flex: 1 }}
    />
    {!checked && (
      <Button component={Link} to={to} onClick={onNavigate} size="small">
        <RevaText id={actionLabel} variant="inherit" component="span" />
      </Button>
    )}
  </Stack>
);

const BoardLinkRow = ({ label, url, onCopy, intl }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <RevaText variant="body2" fontWeight={600}>
        {label}
      </RevaText>
      <RevaText
        variant="caption"
        color="textSecondary"
        sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {url}
      </RevaText>
    </Box>
    <IconButton size="small" aria-label={intl.formatMessage({ id: "Copiar" })} onClick={() => onCopy(url)}>
      <ContentCopyRounded fontSize="small" />
    </IconButton>
    <Button
      size="small"
      variant="outlined"
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<TvRounded />}
      sx={{ flexShrink: 0 }}
    >
      {intl.formatMessage({ id: "clubBoard.share.openOnTv" })}
    </Button>
  </Stack>
);

const ShareModal = ({
  competitionId,
  onClose,
  userContext,
  enqueueSnackbar,
  intl,
}) => {
  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);
  const [link, setLink] = useState(null);
  const [linkError, setLinkError] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [togglingBoard, setTogglingBoard] = useState(false);

  const generateLink = useCallback(
    async (competitionDbId) => {
      try {
        const response = await performRequest(
          "POST",
          competitionsGenerateDynamicLinkRoute,
          { competition_id: competitionDbId },
          true,
          userContext.user,
        );
        const generatedLink = (response?.data?.data ?? response?.data)
          ?.short_dynamic_link;
        if (!generatedLink) throw new Error("Dynamic link was not generated");
        setLink(generatedLink);
      } catch (err) {
        setLinkError(true);
      }
    },
    [userContext.user],
  );

  const fetchCompetition = useCallback(async () => {
    const res = await performRequest(
      "POST",
      competitionsShowRoute,
      { competition_uuid: competitionId },
      true,
      userContext.user,
    );
    return res.data.data;
  }, [competitionId, userContext.user]);

  useEffect(() => {
    fetchCompetition()
      .then((data) => {
        setCompetition(data);
        if (data.short_dynamic_link) {
          setLink(data.short_dynamic_link);
        } else {
          // Eventos antiguos que no recibieron link al crearse
          generateLink(data.id);
        }
      })
      .catch(() =>
        enqueueSnackbar(
          intl.formatMessage({
            id: "No se pudo cargar la información del evento.",
          }),
          {
            variant: "error",
          },
        ),
      )
      .finally(() => setLoading(false));
  }, [fetchCompetition, generateLink, enqueueSnackbar, intl]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      enqueueSnackbar(
        intl.formatMessage({
          id: "Link del evento copiado al portapapeles 🏆",
        }),
        { variant: "success" },
      );
    } catch (err) {
      enqueueSnackbar(
        intl.formatMessage({
          id: "No pudimos copiar el link, cópialo manualmente.",
        }),
        {
          variant: "warning",
        },
      );
    }
  };

  const togglePublished = async () => {
    setToggling(true);
    try {
      await performRequest(
        "POST",
        competitionsTogglePublishedRoute,
        { competition_id: competition.id },
        true,
        userContext.user,
      );
      const updated = await fetchCompetition();
      setCompetition(updated);
      enqueueSnackbar(
        intl.formatMessage({
          id: updated.published
            ? "El evento ha sido publicado en la app Reva 🏆"
            : "El evento ya no aparece en la app Reva",
        }),
        { variant: "success" },
      );
    } catch (err) {
      enqueueSnackbar(
        intl.formatMessage({
          id: "No se pudo actualizar el estado de publicación.",
        }),
        {
          variant: "error",
        },
      );
    } finally {
      setToggling(false);
    }
  };

  const toggleBoard = async () => {
    setTogglingBoard(true);
    try {
      const response = await performRequest(
        "POST",
        competitionsToggleBoardRoute,
        { competition_id: competition.id },
        true,
        userContext.user,
      );
      const boardEnabled = Boolean(response.data.board_enabled);
      setCompetition({ ...competition, board_enabled: boardEnabled });
      enqueueSnackbar(
        intl.formatMessage({ id: boardEnabled ? "clubBoard.share.enabled" : "clubBoard.share.disabled" }),
        { variant: "success" },
      );
    } catch (err) {
      enqueueSnackbar(intl.formatMessage({ id: "clubBoard.share.toggleError" }), { variant: "error" });
    } finally {
      setTogglingBoard(false);
    }
  };

  const copyBoardLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar(intl.formatMessage({ id: "clubBoard.share.copied" }), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage({ id: "No pudimos copiar el link, cópialo manualmente." }), {
        variant: "warning",
      });
    }
  };

  const title = (
    <DialogTitle sx={{ pr: 6 }}>
      {intl.formatMessage({ id: "Compartir evento" })}
      <IconButton
        aria-label={intl.formatMessage({ id: "Cerrar" })}
        onClick={onClose}
        sx={{ position: "absolute", right: 12, top: 12 }}
      >
        <CloseRounded />
      </IconButton>
    </DialogTitle>
  );

  if (loading || !competition) {
    return (
      <>
        {title}
        <DialogContent>
          <Box display="flex" justifyContent="center" py={4}>
            {loading && <CircularProgress />}
          </Box>
        </DialogContent>
      </>
    );
  }

  const eventRoute = adminEventRoute(competitionId);
  const checklist = [
    {
      label: "Banner del evento",
      actionLabel: "Agregar",
      to: `${eventRoute}/edit`,
      checked: Boolean(competition.full_banner_url),
    },
    {
      label: "Al menos un torneo",
      actionLabel: "Crear",
      to: adminEventTournamentsRoute(competitionId),
      checked: competition.tournaments_count >= 1,
    },
    {
      label: "Clubes asignados",
      actionLabel: "Asignar",
      to: `${eventRoute}/clubs`,
      checked: competition.establishments_count >= 1,
    },
    {
      label: "Cuenta de pagos conectada",
      actionLabel: "Conectar",
      to: `${eventRoute}/edit`,
      checked: competition.account !== null,
    },
  ];
  const canPublish = checklist.every((item) => item.checked);
  const { published } = competition;
  const boardEnabled = Boolean(competition.board_enabled);
  const venues = competition.establishments?.data ?? competition.establishments ?? [];
  const boardUrl = (club) => `${window.location.origin}${publicEventBoardRoute(competitionId, { club })}`;

  return (
    <>
      {title}
      <DialogContent>
        <Stack spacing={1}>
          <RevaText id="Link del evento" variant="subtitle1" fontWeight={600} />
          <TextField
            value={link ?? ""}
            placeholder={intl.formatMessage({
              id: linkError
                ? "No pudimos generar el link. Contacte con soporte."
                : "Generando link...",
            })}
            error={linkError}
            size="small"
            fullWidth
            onFocus={(event) => event.target.select()}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    startIcon={<ContentCopyRounded />}
                    onClick={copyLink}
                    disabled={!link}
                  >
                    {intl.formatMessage({ id: "Copiar" })}
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <RevaText
            id="Quien tenga el link puede ver el evento en la app Reva, aunque no esté publicado."
            variant="body2"
            color="textSecondary"
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <RevaText
              id="Publicar en la app Reva"
              variant="subtitle1"
              fontWeight={600}
            />
            <Chip
              size="small"
              label={intl.formatMessage({
                id: published ? "Publicado" : "Privado",
              })}
              color={published ? "success" : "default"}
            />
          </Stack>
          <RevaText
            id={
              published
                ? "Visible para todos los jugadores en el listado de torneos de la app."
                : "Aparecerá en el listado de torneos de la app para todos los jugadores."
            }
            variant="body2"
            color="textSecondary"
          />

          {published ? (
            <Box display="flex" justifyContent="flex-end">
              <Button
                color="inherit"
                onClick={togglePublished}
                disabled={toggling}
              >
                {intl.formatMessage({ id: "Despublicar" })}
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ py: 1 }}>
                {checklist.map((item) => (
                  <ChecklistItem
                    key={item.label}
                    {...item}
                    onNavigate={onClose}
                  />
                ))}
              </Box>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="secondary"
                  disableElevation
                  onClick={togglePublished}
                  disabled={!canPublish || toggling}
                  sx={{ color: "#FFFFFF" }}
                >
                  {toggling ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    intl.formatMessage({ id: "Publicar evento" })
                  )}
                </Button>
              </Box>
            </>
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <RevaText id="clubBoard.share.title" variant="subtitle1" fontWeight={600} />
            <Switch
              checked={boardEnabled}
              onChange={toggleBoard}
              disabled={togglingBoard}
              inputProps={{ "aria-label": intl.formatMessage({ id: "clubBoard.share.enable" }) }}
            />
          </Stack>
          <RevaText id="clubBoard.share.description" variant="body2" color="textSecondary" />
          {boardEnabled && !published && (
            <Alert severity="warning">{intl.formatMessage({ id: "clubBoard.share.requiresPublished" })}</Alert>
          )}
          {boardEnabled &&
            (venues.length === 0 ? (
              <RevaText id="clubBoard.share.noVenues" variant="body2" color="textSecondary" />
            ) : (
              <Stack spacing={1.25}>
                {venues.map((venue) => (
                  <BoardLinkRow
                    key={venue.id}
                    label={venue.public_name || venue.name}
                    url={boardUrl(venue.slug || venue.establishment_uuid)}
                    onCopy={copyBoardLink}
                    intl={intl}
                  />
                ))}
                {venues.length > 1 && (
                  <BoardLinkRow
                    label={intl.formatMessage({ id: "clubBoard.allVenues" })}
                    url={boardUrl()}
                    onCopy={copyBoardLink}
                    intl={intl}
                  />
                )}
              </Stack>
            ))}
        </Stack>
      </DialogContent>
    </>
  );
};

export default withSnackbar(withUserContext(injectIntl(ShareModal)));
